import { useState, useEffect, useRef, useCallback } from 'react';
import Papa from 'papaparse';
import { Tooltip } from 'react-tooltip';
import Plotly from 'plotly.js-dist';
import { saveAs } from 'file-saver';
import html2canvas from 'html2canvas';
import './HealthDataVisualizer.css';

// Score-based color: traffic light scheme
const getScoreColor = (score) => {
  if (score < 25) return '#EF4444';   // Red
  if (score <= 75) return '#F59E0B';  // Amber
  return '#10B981';                    // Green
};

// Searchable Single-Select (auto-closes on pick)
const SearchableSingleSelect = ({ options, selected, onSelect, placeholder, id, disabled = false, tooltipData }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  const filteredOptions = options.filter(opt =>
    opt.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const displayText = selected || placeholder;

  return (
    <div className="dropdown-container" id={id} ref={dropdownRef}>
      <div className="dropdown-header" onClick={() => !disabled && setIsOpen(!isOpen)}
        style={{ opacity: disabled ? 0.6 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}>
        <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
          placeholder={displayText}
          onClick={(e) => { e.stopPropagation(); if (!disabled) setIsOpen(true); }}
          className="dropdown-search-input" disabled={disabled} />
        <span className="dropdown-arrow">{isOpen ? '▲' : '▼'}</span>
      </div>
      {isOpen && !disabled && (
        <div className="dropdown-menu">
          {filteredOptions.length > 0 ? filteredOptions.map((opt, index) => (
            <div key={index} className={`dropdown-item ${selected === opt ? 'selected' : ''}`}
              onClick={() => { onSelect(opt); setIsOpen(false); setSearchTerm(''); }}
              data-tooltip-id={tooltipData && tooltipData[opt] ? 'def-tooltip' : undefined}
              data-tooltip-content={tooltipData && tooltipData[opt] ? tooltipData[opt] : undefined}
            >
              <input type="radio" checked={selected === opt} readOnly />
              <span>{opt}</span>
            </div>
          )) : <div className="dropdown-item no-options">No options found</div>}
        </div>
      )}
    </div>
  );
};

// Searchable Multi-Select with tooltips
const SearchableMultiSelect = ({ options, selected, onToggle, onSelectAll, onDeselectAll, placeholder, id, disabled = false, tooltipData }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  const filteredOptions = options.filter(opt => opt.toLowerCase().includes(searchTerm.toLowerCase()));

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getDisplayText = () => {
    if (selected.length === 0) return 'None Selected';
    if (selected.length === options.length && options.length > 0) return 'All Selected';
    if (selected.length === 1) return selected[0];
    return `${selected.length} Selected`;
  };

  return (
    <div className="dropdown-container" id={id} ref={dropdownRef}>
      <div className="dropdown-header" onClick={() => !disabled && setIsOpen(!isOpen)}
        style={{ opacity: disabled ? 0.6 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}>
        <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
          placeholder={disabled ? placeholder : getDisplayText()}
          onClick={(e) => { e.stopPropagation(); if (!disabled) setIsOpen(true); }}
          className="dropdown-search-input" disabled={disabled} />
        <span className="dropdown-arrow">{isOpen ? '▲' : '▼'}</span>
      </div>
      {isOpen && !disabled && (
        <div className="dropdown-menu">
          <div className="dropdown-actions">
            <button className="dropdown-action-button" onClick={onSelectAll}>Select All</button>
            <button className="dropdown-action-button" onClick={onDeselectAll}>Deselect All</button>
          </div>
          {filteredOptions.length > 0 ? filteredOptions.map((opt, index) => (
            <div key={index} className={`dropdown-item ${selected.includes(opt) ? 'selected' : ''}`}
              onClick={() => onToggle(opt)}
              data-tooltip-id={tooltipData && tooltipData[opt] ? 'def-tooltip' : undefined}
              data-tooltip-content={tooltipData && tooltipData[opt] ? tooltipData[opt] : undefined}
            >
              <input type="checkbox" checked={selected.includes(opt)} readOnly />
              <span>{opt}</span>
            </div>
          )) : <div className="dropdown-item no-options">No options found</div>}
        </div>
      )}
    </div>
  );
};

const Assessment = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [dataLoaded, setDataLoaded] = useState(false);

  const [dataStructure, setDataStructure] = useState({
    countries: [], domains: [], components: [], subcomponents: [], elements: []
  });

  // Available options (cascading)
  const [availableDomains, setAvailableDomains] = useState([]);
  const [availableComponents, setAvailableComponents] = useState([]);
  const [availableSubcomponents, setAvailableSubcomponents] = useState([]);
  const [availableElements, setAvailableElements] = useState([]);

  // Single-select for Country, Domain, Component, Subcomponent
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [selectedSubcomponent, setSelectedSubcomponent] = useState(null);
  // Multi-select for Elements only
  const [selectedElements, setSelectedElements] = useState([]);

  // Tooltip definition maps
  const [domainDefs, setDomainDefs] = useState({});
  const [componentDefs, setComponentDefs] = useState({});
  const [subcomponentDefs, setSubcomponentDefs] = useState({});
  const [elementDefs, setElementDefs] = useState({});

  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [showData, setShowData] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });

  const chartRef = useRef(null);
  const contentAreaRef = useRef(null);

  const exportToPNG = async () => {
    const contentArea = contentAreaRef.current;
    if (!contentArea) return;
    try {
      const canvas = await html2canvas(contentArea, { scale: 2, backgroundColor: '#ffffff', logging: false, useCORS: true });
      const finalCanvas = document.createElement('canvas');
      const ctx = finalCanvas.getContext('2d');
      finalCanvas.width = canvas.width;
      finalCanvas.height = canvas.height;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, finalCanvas.width, finalCanvas.height);
      ctx.drawImage(canvas, 0, 0);
      ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
      ctx.font = 'italic 22px Arial, sans-serif';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'bottom';
      ctx.fillText(`\u00A9 Health Systems Innovation Lab, Harvard University, ${new Date().getFullYear()}`, 40, canvas.height - 30);
      const logo = new Image();
      logo.crossOrigin = 'anonymous';
      logo.onload = () => {
        const logoHeight = 50;
        const logoWidth = logoHeight * (logo.width / logo.height);
        ctx.drawImage(logo, finalCanvas.width - logoWidth - 40, canvas.height - logoHeight - 20, logoWidth, logoHeight);
        finalCanvas.toBlob(blob => saveAs(blob, `t1d_assessment_${new Date().toISOString().split('T')[0]}.png`));
      };
      logo.onerror = () => finalCanvas.toBlob(blob => saveAs(blob, `t1d_assessment_${new Date().toISOString().split('T')[0]}.png`));
      logo.src = '/logo.png';
    } catch (error) {
      console.error('Error exporting to PNG:', error);
      alert('Failed to export image.');
    }
  };

  // Load CSV data with random initial selection
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const csvResponse = await fetch('/Current/t1dataset_assesment.csv');
        if (!csvResponse.ok) throw new Error(`HTTP error: ${csvResponse.status}`);
        const csvText = await csvResponse.text();

        Papa.parse(csvText, {
          header: true,
          dynamicTyping: false,
          skipEmptyLines: true,
          complete: results => {
            const cleanedData = results.data
              .filter(row => row.Country && row.Elements && row['Element Score (%)'] !== undefined && row['Element Score (%)'] !== '')
              .map(row => ({
                Country: (row.Country || '').trim(),
                Domain: (row.Domain || '').trim(),
                DomainDefinition: (row['Domain Definition'] || '').trim(),
                Component: (row.Component || '').trim(),
                ComponentDefinition: (row['Component  Defintion'] || '').trim(),
                Subcomponent: (row['Subcomponent '] || row.Subcomponent || '').trim(),
                SubcomponentDefinition: (row['Subcomponent definition'] || '').trim(),
                Elements: (row.Elements || '').trim(),
                ElementDefinition: (row['Element Definition'] || '').trim(),
                ElementScore: parseFloat(row['Element Score (%)']) || 0,
                ElementRawScore: parseFloat(row['Element Raw Score '] || row['Element Raw Score']) || 0,
                TotalClinics: parseInt(row['Total Clinics Within Country']) || 0
              }))
              .filter(row => row.Country && row.Elements);

            // Build definition maps for tooltips
            const dDefs = {}, cDefs = {}, sDefs = {}, eDefs = {};
            cleanedData.forEach(row => {
              if (row.Domain && row.DomainDefinition) dDefs[row.Domain] = row.DomainDefinition;
              if (row.Component && row.ComponentDefinition) cDefs[row.Component] = row.ComponentDefinition;
              if (row.Subcomponent && row.SubcomponentDefinition) sDefs[row.Subcomponent] = row.SubcomponentDefinition;
              if (row.Elements && row.ElementDefinition) eDefs[row.Elements] = row.ElementDefinition;
            });

            const structure = {
              countries: [...new Set(cleanedData.map(row => row.Country))].filter(Boolean).sort(),
              domains: [...new Set(cleanedData.map(row => row.Domain))].filter(Boolean).sort(),
              components: [...new Set(cleanedData.map(row => row.Component))].filter(Boolean).sort(),
              subcomponents: [...new Set(cleanedData.map(row => row.Subcomponent))].filter(Boolean).sort(),
              elements: [...new Set(cleanedData.map(row => row.Elements))].filter(Boolean).sort()
            };

            // Random cascading initial selection
            let initCountry = null, initDomain = null, initComponent = null, initSubcomponent = null;
            let initElements = [];
            let initAvailDomains = [], initAvailComponents = [], initAvailSubcomponents = [], initAvailElements = [];

            if (structure.countries.length > 0) {
              initCountry = structure.countries[Math.floor(Math.random() * structure.countries.length)];
              const countryData = cleanedData.filter(row => row.Country === initCountry);
              initAvailDomains = [...new Set(countryData.map(row => row.Domain))].filter(Boolean).sort();

              if (initAvailDomains.length > 0) {
                initDomain = initAvailDomains[Math.floor(Math.random() * initAvailDomains.length)];
                const domainData = countryData.filter(row => row.Domain === initDomain);
                initAvailComponents = [...new Set(domainData.map(row => row.Component))].filter(Boolean).sort();

                if (initAvailComponents.length > 0) {
                  initComponent = initAvailComponents[Math.floor(Math.random() * initAvailComponents.length)];
                  const componentData = domainData.filter(row => row.Component === initComponent);
                  initAvailSubcomponents = [...new Set(componentData.map(row => row.Subcomponent))].filter(Boolean).sort();

                  if (initAvailSubcomponents.length > 0) {
                    initSubcomponent = initAvailSubcomponents[Math.floor(Math.random() * initAvailSubcomponents.length)];
                    const subcompData = componentData.filter(row => row.Subcomponent === initSubcomponent);
                    initAvailElements = [...new Set(subcompData.map(row => row.Elements))].filter(Boolean).sort();
                    initElements = initAvailElements;
                  }
                }
              }
            }

            setData(cleanedData);
            setDataStructure(structure);
            setDomainDefs(dDefs);
            setComponentDefs(cDefs);
            setSubcomponentDefs(sDefs);
            setElementDefs(eDefs);
            setAvailableDomains(initAvailDomains);
            setAvailableComponents(initAvailComponents);
            setAvailableSubcomponents(initAvailSubcomponents);
            setAvailableElements(initAvailElements);
            setSelectedCountry(initCountry);
            setSelectedDomain(initDomain);
            setSelectedComponent(initComponent);
            setSelectedSubcomponent(initSubcomponent);
            setSelectedElements(initElements);
            setLoading(false);
            setTimeout(() => setDataLoaded(true), 50);
          },
          error: error => { console.error('Error parsing CSV:', error); setLoading(false); }
        });
      } catch (error) { console.error('Error loading data:', error); setLoading(false); }
    };
    loadData();
  }, []);

  // Cascade: Country -> Domains
  useEffect(() => {
    if (!dataLoaded || data.length === 0) return;
    if (!selectedCountry) {
      setAvailableDomains([]);
      setSelectedDomain(null);
      return;
    }
    const countryData = data.filter(row => row.Country === selectedCountry);
    const domains = [...new Set(countryData.map(row => row.Domain))].filter(Boolean).sort();
    setAvailableDomains(domains);
    setSelectedDomain(prev => domains.includes(prev) ? prev : null);
  }, [selectedCountry, dataLoaded, data]);

  // Cascade: Domain -> Components
  useEffect(() => {
    if (!dataLoaded || data.length === 0) return;
    if (!selectedCountry || !selectedDomain) {
      setAvailableComponents([]);
      setSelectedComponent(null);
      return;
    }
    const filtered = data.filter(row => row.Country === selectedCountry && row.Domain === selectedDomain);
    const components = [...new Set(filtered.map(row => row.Component))].filter(Boolean).sort();
    setAvailableComponents(components);
    setSelectedComponent(prev => components.includes(prev) ? prev : null);
  }, [selectedDomain, selectedCountry, dataLoaded, data]);

  // Cascade: Component -> Subcomponents
  useEffect(() => {
    if (!dataLoaded || data.length === 0) return;
    if (!selectedCountry || !selectedDomain || !selectedComponent) {
      setAvailableSubcomponents([]);
      setSelectedSubcomponent(null);
      return;
    }
    const filtered = data.filter(row =>
      row.Country === selectedCountry && row.Domain === selectedDomain && row.Component === selectedComponent
    );
    const subcomponents = [...new Set(filtered.map(row => row.Subcomponent))].filter(Boolean).sort();
    setAvailableSubcomponents(subcomponents);
    setSelectedSubcomponent(prev => subcomponents.includes(prev) ? prev : null);
  }, [selectedComponent, selectedCountry, selectedDomain, dataLoaded, data]);

  // Cascade: Subcomponent -> Elements
  useEffect(() => {
    if (!dataLoaded || data.length === 0) return;
    if (!selectedCountry || !selectedDomain || !selectedComponent || !selectedSubcomponent) {
      setAvailableElements([]);
      setSelectedElements([]);
      return;
    }
    const filtered = data.filter(row =>
      row.Country === selectedCountry && row.Domain === selectedDomain &&
      row.Component === selectedComponent && row.Subcomponent === selectedSubcomponent
    );
    const elements = [...new Set(filtered.map(row => row.Elements))].filter(Boolean).sort();
    setAvailableElements(elements);
    setSelectedElements(prev => {
      const valid = prev.filter(e => elements.includes(e));
      return valid.length > 0 ? valid : elements;
    });
  }, [selectedSubcomponent, selectedCountry, selectedDomain, selectedComponent, dataLoaded, data]);

  // Filter data for chart
  useEffect(() => {
    if (data.length === 0 || !selectedCountry || !selectedDomain ||
        !selectedComponent || !selectedSubcomponent || selectedElements.length === 0) {
      setFilteredData([]);
      return;
    }
    const filtered = data.filter(row =>
      row.Country === selectedCountry && row.Domain === selectedDomain &&
      row.Component === selectedComponent && row.Subcomponent === selectedSubcomponent &&
      selectedElements.includes(row.Elements)
    );
    setFilteredData(filtered);
  }, [data, selectedCountry, selectedDomain, selectedComponent, selectedSubcomponent, selectedElements]);

  // Responsive resize
  const resizeChart = useCallback(() => {
    if (chartRef.current) Plotly.Plots.resize(chartRef.current);
  }, []);

  useEffect(() => {
    window.addEventListener('resize', resizeChart);
    return () => window.removeEventListener('resize', resizeChart);
  }, [resizeChart]);

  useEffect(() => {
    const timer = setTimeout(resizeChart, 350);
    return () => clearTimeout(timer);
  }, [isSidebarOpen, resizeChart]);

  // Render chart
  useEffect(() => {
    if (!chartRef.current || filteredData.length === 0) {
      if (chartRef.current) Plotly.purge(chartRef.current);
      return;
    }
    renderBarChart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredData]);

  const renderBarChart = () => {
    // Aggregate scores per element
    const elementScores = {};
    filteredData.forEach(row => {
      if (!elementScores[row.Elements]) elementScores[row.Elements] = { scores: [], definition: row.ElementDefinition };
      elementScores[row.Elements].scores.push(row.ElementScore);
    });

    // Sort elements by average score ascending
    const sortedEntries = Object.entries(elementScores)
      .map(([el, data]) => ({
        element: el,
        avgScore: data.scores.reduce((a, b) => a + b, 0) / data.scores.length,
        definition: data.definition
      }))
      .sort((a, b) => a.avgScore - b.avgScore);

    const fullElements = sortedEntries.map(e => e.element);
    const scores = sortedEntries.map(e => e.avgScore);
    const definitions = sortedEntries.map(e => e.definition);
    const colors = scores.map(s => getScoreColor(s));

    // Truncate long x-axis labels (full name shows on hover)
    const truncate = (str, max) => str.length > max ? str.substring(0, max) + '...' : str;
    const tickLabels = fullElements.map(el => truncate(el, 35));

    const trace = {
      x: tickLabels,
      y: scores,
      type: 'bar',
      marker: { color: colors, line: { color: 'white', width: 1 } },
      text: scores.map(s => `${s.toFixed(0)}%`),
      textposition: 'outside',
      textfont: { size: 11, color: '#374151' },
      hovertemplate: fullElements.map((el, idx) =>
        `<b>${el}</b><br>Score: ${scores[idx].toFixed(1)}%<br><i>${definitions[idx] ? definitions[idx].substring(0, 120) : ''}</i><extra></extra>`
      )
    };

    const layout = {
      title: {
        text: `<b>Type 1 Diabetes Readiness Assessment</b><br><span style="font-size:13px;color:#6B7280">${selectedCountry} \u2014 ${selectedSubcomponent}</span>`,
        font: { size: 24, color: '#374151' },
        x: 0.5
      },
      xaxis: {
        title: { text: 'Assessment Elements', font: { size: 12, color: '#6B7280' } },
        tickangle: -30,
        tickfont: { size: 9, color: '#6B7280' },
        automargin: false
      },
      yaxis: {
        title: { text: 'Element Score (%)', font: { size: 12, color: '#6B7280' } },
        range: [0, 110],
        tickfont: { size: 11, color: '#6B7280' },
        ticksuffix: '%',
        showgrid: true,
        gridcolor: '#E5E7EB'
      },
      showlegend: false,
      hovermode: 'closest',
      plot_bgcolor: 'white',
      paper_bgcolor: 'white',
      margin: { l: 55, r: 15, t: 70, b: 140 },
      autosize: true,
      bargap: 0.3
    };

    Plotly.newPlot(chartRef.current, [trace], layout, {
      responsive: true,
      displaylogo: false,
      displayModeBar: false
    });
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });
  };

  const toggleData = () => setShowData(!showData);

  const handleElementToggle = (element) => {
    setSelectedElements(prev =>
      prev.includes(element) ? prev.filter(e => e !== element) : [...prev, element]
    );
  };

  let tableData = [...filteredData];
  if (sortConfig.key) {
    tableData.sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];
      if (typeof aVal === 'string') return sortConfig.direction === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }

  if (loading) return <div className="loading-container"><div className="loading-message">Loading T1D Assessment Data...</div></div>;

  const hasAllSelections = selectedCountry && selectedDomain &&
    selectedComponent && selectedSubcomponent && selectedElements.length > 0;

  return (
    <div className="explorer-container">
      {/* Sidebar Toggle Button - floating top-left */}
      <button
        className="sidebar-toggle-btn"
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        title={isSidebarOpen ? 'Collapse panel' : 'Expand panel'}
      >
        {isSidebarOpen ? '\u25C0' : '\u25B6'}
      </button>

      {isSidebarOpen && (
        <div className="sidebar open">
          <div className="sidebar-content">
            <div className="section">
              <h2 className="section-title"
                data-tooltip-id="def-tooltip" data-tooltip-content="Select a country to view its T1D readiness assessment">
                Country
              </h2>
              <SearchableSingleSelect
                options={dataStructure.countries}
                selected={selectedCountry}
                onSelect={setSelectedCountry}
                placeholder="Select Country"
                id="countries-select"
              />
            </div>

            <div className="section">
              <h2 className="section-title"
                data-tooltip-id="def-tooltip" data-tooltip-content="Assessment domains represent broad areas of T1D readiness">
                Domain
              </h2>
              <SearchableSingleSelect
                options={availableDomains}
                selected={selectedDomain}
                onSelect={setSelectedDomain}
                placeholder={selectedCountry ? "Select Domain" : "Select Country first"}
                id="domains-select"
                disabled={!selectedCountry}
                tooltipData={domainDefs}
              />
            </div>

            <div className="section">
              <h2 className="section-title"
                data-tooltip-id="def-tooltip" data-tooltip-content="Components are specific focus areas within each domain">
                Component
              </h2>
              <SearchableSingleSelect
                options={availableComponents}
                selected={selectedComponent}
                onSelect={setSelectedComponent}
                placeholder={selectedDomain ? "Select Component" : "Select Domain first"}
                id="components-select"
                disabled={!selectedDomain}
                tooltipData={componentDefs}
              />
            </div>

            <div className="section">
              <h2 className="section-title"
                data-tooltip-id="def-tooltip" data-tooltip-content="Subcomponents break down components into detailed assessment areas">
                Subcomponent
              </h2>
              <SearchableSingleSelect
                options={availableSubcomponents}
                selected={selectedSubcomponent}
                onSelect={setSelectedSubcomponent}
                placeholder={selectedComponent ? "Select Subcomponent" : "Select Component first"}
                id="subcomponents-select"
                disabled={!selectedComponent}
                tooltipData={subcomponentDefs}
              />
            </div>

            <div className="section">
              <h2 className="section-title"
                data-tooltip-id="def-tooltip" data-tooltip-content="Individual assessment elements scored as percentage of readiness">
                Elements
              </h2>
              <SearchableMultiSelect
                options={availableElements}
                selected={selectedElements}
                onToggle={handleElementToggle}
                onSelectAll={() => setSelectedElements(availableElements)}
                onDeselectAll={() => setSelectedElements([])}
                placeholder={selectedSubcomponent ? "Select Elements" : "Select Subcomponent first"}
                id="elements-select"
                disabled={!selectedSubcomponent}
                tooltipData={elementDefs}
              />
              {selectedSubcomponent && (
                <span style={{ fontSize: '0.75rem', color: '#6B7280' }}>
                  {selectedElements.length} of {availableElements.length} selected
                </span>
              )}
            </div>

            <div className="section">
              <button className="button button-secondary" onClick={toggleData} style={{ width: '100%' }}>
                {showData ? 'Hide Data Table' : 'Show Data Table'}
              </button>
            </div>

            {/* Metadata Section */}
            <div className="section metadata-section">
              <h2 className="section-title">Metadata</h2>
              <div className="metadata-content">
                <div className="metadata-row">
                  <span className="metadata-label">Dataset</span>
                  <span className="metadata-value">READY T1D Assessment</span>
                </div>
                <div className="metadata-row">
                  <span className="metadata-label">Source</span>
                  <span className="metadata-value">t1dataset_assesment.csv</span>
                </div>
                {selectedCountry && (
                  <div className="metadata-row">
                    <span className="metadata-label">Country</span>
                    <span className="metadata-value">{selectedCountry}</span>
                  </div>
                )}
                {selectedDomain && (
                  <div className="metadata-row">
                    <span className="metadata-label">Domain</span>
                    <span className="metadata-value">{selectedDomain}</span>
                  </div>
                )}
                {selectedComponent && (
                  <div className="metadata-row">
                    <span className="metadata-label">Component</span>
                    <span className="metadata-value">{selectedComponent}</span>
                  </div>
                )}
                <div className="metadata-row">
                  <span className="metadata-label">Elements</span>
                  <span className="metadata-value">{selectedElements.length} of {availableElements.length}</span>
                </div>
                <div className="metadata-row">
                  <span className="metadata-label">Records</span>
                  <span className="metadata-value">{filteredData.length}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="main-content">
        {hasAllSelections && (
          <button onClick={exportToPNG} title="Download chart as PNG"
            style={{ position: 'fixed', top: 70, right: 24, zIndex: 100, display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 24px', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '6px', cursor: 'pointer', fontSize: '18px', color: '#495057', fontWeight: '600', boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            PNG
          </button>
        )}

        <div className="content-area" ref={contentAreaRef} style={{ position: 'relative' }}>
          {!hasAllSelections ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#6B7280', fontSize: '1.1rem' }}>
              <p style={{ marginBottom: '1rem' }}>Please select all required options to display the chart:</p>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                <li style={{ color: selectedCountry ? '#10B981' : '#EF4444' }}>{selectedCountry ? '\u2713' : '\u25CB'} Country</li>
                <li style={{ color: selectedDomain ? '#10B981' : '#EF4444' }}>{selectedDomain ? '\u2713' : '\u25CB'} Domain</li>
                <li style={{ color: selectedComponent ? '#10B981' : '#EF4444' }}>{selectedComponent ? '\u2713' : '\u25CB'} Component</li>
                <li style={{ color: selectedSubcomponent ? '#10B981' : '#EF4444' }}>{selectedSubcomponent ? '\u2713' : '\u25CB'} Subcomponent</li>
                <li style={{ color: selectedElements.length > 0 ? '#10B981' : '#EF4444' }}>{selectedElements.length > 0 ? '\u2713' : '\u25CB'} Elements ({selectedElements.length} selected)</li>
              </ul>
            </div>
          ) : (
            <>
              <div ref={chartRef} style={{ width: '100%', height: '100%' }} />
              {/* Score Legend - top left of chart */}
              <div style={{
                position: 'absolute', top: 10, left: 12, zIndex: 5,
                background: 'rgba(255,255,255,0.92)', border: '1px solid #E5E7EB',
                borderRadius: 8, padding: '12px 16px',
                display: 'flex', flexDirection: 'column', gap: 6,
                boxShadow: '0 2px 6px rgba(0,0,0,0.1)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: '0.875rem' }}>
                  <span style={{ width: 20, height: 20, borderRadius: 3, backgroundColor: '#EF4444', flexShrink: 0 }} />
                  <span style={{ color: '#374151', fontWeight: 500 }}>&lt; 25%</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: '0.875rem' }}>
                  <span style={{ width: 20, height: 20, borderRadius: 3, backgroundColor: '#F59E0B', flexShrink: 0 }} />
                  <span style={{ color: '#374151', fontWeight: 500 }}>25 – 75%</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: '0.875rem' }}>
                  <span style={{ width: 20, height: 20, borderRadius: 3, backgroundColor: '#10B981', flexShrink: 0 }} />
                  <span style={{ color: '#374151', fontWeight: 500 }}>&gt; 75%</span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {showData && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.9)', zIndex: 10001, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }} onClick={toggleData}>
          <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '2rem', maxWidth: '90vw', maxHeight: '80vh', width: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
              <div>
                <h3 style={{ margin: '0 0 4px 0', fontSize: '1.25rem', fontWeight: '600', color: '#111827' }}>
                  T1D Readiness Assessment — {selectedCountry}
                </h3>
                <p style={{ margin: 0, fontSize: '0.875rem', color: '#6B7280' }}>
                  {selectedDomain} &middot; {selectedComponent} &middot; {filteredData.length} records
                </p>
              </div>
              <button onClick={toggleData} style={{ backgroundColor: 'transparent', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#6B7280', alignSelf: 'flex-start' }}>&times;</button>
            </div>
            <div style={{ flex: 1, overflow: 'auto', border: '1px solid #E5E7EB', borderRadius: '4px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead style={{ position: 'sticky', top: 0, backgroundColor: '#F3F4F6' }}>
                  <tr>
                    <th style={{ padding: '12px', border: '1px solid #D1D5DB', textAlign: 'left', cursor: 'pointer' }} onClick={() => handleSort('Country')}>Country {sortConfig.key === 'Country' ? (sortConfig.direction === 'asc' ? '\u25B2' : '\u25BC') : ''}</th>
                    <th style={{ padding: '12px', border: '1px solid #D1D5DB', textAlign: 'left', cursor: 'pointer' }} onClick={() => handleSort('Domain')}>Domain {sortConfig.key === 'Domain' ? (sortConfig.direction === 'asc' ? '\u25B2' : '\u25BC') : ''}</th>
                    <th style={{ padding: '12px', border: '1px solid #D1D5DB', textAlign: 'left', cursor: 'pointer' }} onClick={() => handleSort('Component')}>Component {sortConfig.key === 'Component' ? (sortConfig.direction === 'asc' ? '\u25B2' : '\u25BC') : ''}</th>
                    <th style={{ padding: '12px', border: '1px solid #D1D5DB', textAlign: 'left', cursor: 'pointer' }} onClick={() => handleSort('Subcomponent')}>Subcomponent {sortConfig.key === 'Subcomponent' ? (sortConfig.direction === 'asc' ? '\u25B2' : '\u25BC') : ''}</th>
                    <th style={{ padding: '12px', border: '1px solid #D1D5DB', textAlign: 'left', cursor: 'pointer' }} onClick={() => handleSort('Elements')}>Element {sortConfig.key === 'Elements' ? (sortConfig.direction === 'asc' ? '\u25B2' : '\u25BC') : ''}</th>
                    <th style={{ padding: '12px', border: '1px solid #D1D5DB', textAlign: 'right', cursor: 'pointer' }} onClick={() => handleSort('ElementScore')}>Score (%) {sortConfig.key === 'ElementScore' ? (sortConfig.direction === 'asc' ? '\u25B2' : '\u25BC') : ''}</th>
                  </tr>
                </thead>
                <tbody>
                  {tableData.map((row, index) => (
                    <tr key={index} style={{ backgroundColor: index % 2 === 0 ? '#FFFFFF' : '#F9FAFB' }}>
                      <td style={{ padding: '8px 12px', border: '1px solid #E5E7EB' }}>{row.Country}</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #E5E7EB' }}>{row.Domain}</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #E5E7EB' }}>{row.Component}</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #E5E7EB' }}>{row.Subcomponent}</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #E5E7EB' }}>{row.Elements}</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #E5E7EB', textAlign: 'right' }}>{row.ElementScore.toFixed(1)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      <Tooltip id="def-tooltip" place="right" style={{ maxWidth: '300px', fontSize: '0.8rem' }} />
    </div>
  );
};

export default Assessment;
